import { DecisionDetails } from '@/components/legislation-decisions/decision-details';
import DecisionsSidebar from '@/components/legislation-decisions/decisions-sidebar';
import PdfViewer from '@/components/legislation-decisions/pdf-viewer';
import type { LegislationConnectedDecisionsDialogProps } from '@/components/legislation-decisions/types';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import HtmlViewer from '@/elements/html-viewer/html-viewer';
import legislationServices from '@/services/legislation-services';
import type { ReferencedDecisionsInterface } from '@/types/api-types';
import { File, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

/**
 * Main dialog component for legislation connected decisions
 */
export function LegislationConnectedDecisionsDialog({
  refId,
  open,
  setOpen,
}: LegislationConnectedDecisionsDialogProps) {
  // Initialize translations
  const t = useTranslations('legislationDecisions');

  // State management
  const [decisions, setDecisions] = useState<ReferencedDecisionsInterface[]>(
    []
  );
  const [selectedDecision, setSelectedDecision] =
    useState<ReferencedDecisionsInterface | null>(null);
  const [fileUrl, setFileUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [pdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [zoom] = useState(80);
  const [currentPage, setCurrentPage] = useState(1);
  const [, setTotalPages] = useState(1);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) return;

    // Reset state when dialog opens with a new refId
    setDecisions([]);
    setSelectedDecision(null);
    setFileUrl('');
    setPdfError(null);
    setPage(1);
    setHasMore(true);
  }, [open, refId]);

  // Fetch decisions data with pagination
  useEffect(() => {
    const fetchDecisions = async () => {
      if (!open) return;

      setIsLoading(true);
      try {
        const response =
          await legislationServices.getLegislationReferencedDecisions(
            refId,
            page,
            10
          );

        // Append new decisions to existing list
        setDecisions((prev) => [...prev, ...response.data]);

        // Check if we've reached the end of available data
        setHasMore(response.data.length > 0);

        // Select the first decision by default if none is selected
        const firstDecision = response.data[0];
        if (response.data.length > 0 && !selectedDecision && firstDecision) {
          setSelectedDecision(firstDecision);
        }
      } catch (error) {
        console.error('Error fetching decisions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDecisions();
  }, [refId, open, page]);

  // Judikaty decrees render as secHtml + sudHtml HTML panes; all other decrees render
  // their PDF. A row exposes one shape or the other (signed URLs come from the backend).
  const isHtmlDecision = Boolean(
    selectedDecision?.secHtml_url || selectedDecision?.sudHtml_url
  );

  // Load PDF URL for the selected (PDF) decision. HTML decisions are handled by
  // HtmlViewer below and don't use fileUrl.
  useEffect(() => {
    if (!selectedDecision) return;

    if (isHtmlDecision) {
      setFileUrl('');
      setPdfError(null);
    } else if (selectedDecision.pdf_url) {
      setFileUrl(selectedDecision.pdf_url);
      setPdfError(null);
    } else {
      setFileUrl('');
      setPdfError(t('errors.failedToLoadUrl'));
    }
  }, [selectedDecision, isHtmlDecision, t]);

  // Handle decision selection
  const handleSelectDecision = useCallback(
    (decision: ReferencedDecisionsInterface) => {
      setSelectedDecision(decision);
      setCurrentPage(1);
    },
    []
  );

  // Handle infinite scroll for decision list
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      // Load more when user scrolls to bottom (with a small threshold)
      const threshold = 50;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < threshold;

      if (isNearBottom && hasMore && !isLoading) {
        setPage((prev) => prev + 1);
      }
    },
    [hasMore, isLoading]
  );

  // Handle PDF page change
  const handlePageChange = (pageNum: number) => {
    setCurrentPage(pageNum);
  };

  // Handle document load success
  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className='max-w-6xl max-h-[90vh]'
        aria-describedby={undefined}
      >
        <DialogTitle className='sr-only'>
          {t('header.titleForSideBar')}
        </DialogTitle>
        {isLoading && !selectedDecision ? (
          <div className='flex h-[85vh] items-center justify-center'>
            <div className='text-status-info flex items-center gap-2'>
              <Loader2 className='animate-spin h-5 w-5' />
              {t('states.loadingDetails')}
            </div>
          </div>
        ) : (
          <div className='flex h-[85vh] overflow-hidden'>
            {/* Sidebar with decisions list */}
            <DecisionsSidebar
              decisions={decisions}
              selectedDecision={selectedDecision}
              isLoading={isLoading}
              onSelectDecision={handleSelectDecision}
              onScroll={handleScroll}
            />

            {/* Main content area with PDF viewer */}
            <div className='flex-1 flex flex-col overflow-hidden'>
              {selectedDecision && (
                <DecisionDetails
                  selectedDecision={selectedDecision}
                  t={t as (key: string) => string}
                />
              )}

              <div className='flex-1 relative overflow-hidden'>
                {pdfLoading ? (
                  <div className='flex flex-col items-center justify-center h-full'>
                    <Loader2 className='w-12 h-12 text-status-info animate-spin mb-4' />
                    <p className='text-sm text-content-secondary'>
                      {t('states.loadingDocument')}
                    </p>
                  </div>
                ) : selectedDecision && isHtmlDecision ? (
                  // Judikaty: secHtml then sudHtml (HtmlViewer renders both panes).
                  <div className='h-full overflow-auto'>
                    <HtmlViewer
                      secHtmlStoragePath={
                        selectedDecision.secHtml_url ?? undefined
                      }
                      sudHtmlStoragePath={
                        selectedDecision.sudHtml_url ?? undefined
                      }
                    />
                  </div>
                ) : selectedDecision ? (
                  <PdfViewer
                    fileUrl={fileUrl}
                    isLoading={pdfLoading}
                    error={pdfError}
                    zoom={zoom}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    onDocumentLoadSuccess={handleDocumentLoadSuccess}
                  />
                ) : (
                  <div className='flex flex-col items-center justify-center h-full'>
                    <File className='w-16 h-16 text-content-disabled mb-4' />
                    <p className='text-content-muted'>
                      {t('states.selectDecision')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
